import concat from 'lodash/concat'
import forEach from 'lodash/forEach'
import has from 'lodash/has'
import indexOf from 'lodash/indexOf'
import isNull from 'lodash/isNull'
import { TREE_LEVELS } from '../../../services/constants'

export type LocationTreeNode = {
  id: number
  name: string
  locationType: string
  children?: LocationTreeNode[]
  checked?: boolean
  indeterminate?: boolean
  propertyId?: number
  isParent?: boolean
  expanded?: boolean
}

export type PickerOrganization = {
  id: number
  name: string
}

export type LocationFilter = {
  selectedName: string
  selectedBuildings: number[] | null
}

export type LocationFilterParams = LocationFilter & {
  indeterminateProperties: number[]
}

const LOCATION_LABELS = {
  allProperties: 'All Properties',
  multipleProperties: 'Multiple Properties',
}

export const getLocationFilterParams = (
  treeRoot: LocationTreeNode,
  allowedLocationIds: number[] | null | undefined
): LocationFilterParams => {
  const params: LocationFilterParams = {
    selectedBuildings: allowedLocationIds ?? null,
    selectedName: '',
    indeterminateProperties: [],
  }

  if (isNull(allowedLocationIds)) {
    return { ...params, selectedName: LOCATION_LABELS.allProperties }
  }

  if (!allowedLocationIds?.length) {
    return params
  }

  if (allowedLocationIds.length > 1) {
    return { ...params, selectedName: LOCATION_LABELS.multipleProperties }
  }

  const selectedName = getLocationNameById(
    treeRoot.children || [],
    allowedLocationIds[0]
  )

  return { ...params, selectedName: selectedName || '' }
}

export const prepareLocationFiltersForDevice = (
  nodes: LocationTreeNode[],
  userPref?: number[] | null
): LocationFilterParams => {
  const nodeList = concat([], nodes)
  const isAllByUserPref = isNull(userPref)
  const params: LocationFilterParams = {
    selectedName: '',
    selectedBuildings: isAllByUserPref ? null : [],
    indeterminateProperties: [],
  }
  let isParentChecked = false
  let propertyId: number | undefined

  const traverse = (current: LocationTreeNode[]) => {
    current.forEach((item) => {
      const hasChecked = has(item, 'checked')
      const inUserPref = userPref ? indexOf(userPref, item.id) !== -1 : false
      const isChecked = hasChecked ? !!item.checked : inUserPref

      if (item.locationType === TREE_LEVELS.PROPERTY) {
        propertyId = item.id
      } else {
        item.propertyId = propertyId
      }

      if (item.children && item.children.length) {
        if (isChecked) {
          if (!isParentChecked || item.locationType === TREE_LEVELS.PROPERTY) {
            params.selectedBuildings?.push(item.id)
            params.selectedName = item.name
          }

          if (
            item.locationType !== TREE_LEVELS.PROPERTY &&
            propertyId &&
            indexOf(params.indeterminateProperties, propertyId) === -1
          ) {
            params.indeterminateProperties.push(propertyId)
          }
        } else {
          isParentChecked = false

          if (
            item.locationType === TREE_LEVELS.PROPERTY &&
            item.indeterminate
          ) {
            params.indeterminateProperties.push(item.id)
          }

          traverse(item.children)
        }
      } else if (isChecked) {
        if (!isParentChecked && item.locationType !== TREE_LEVELS.PROPERTY) {
          if (
            propertyId &&
            indexOf(params.indeterminateProperties, propertyId) === -1
          ) {
            params.indeterminateProperties.push(propertyId)
          }
        }

        if (!isParentChecked || item.locationType === TREE_LEVELS.PROPERTY) {
          params.selectedBuildings?.push(item.id)
          params.selectedName = item.name
        }
      } else {
        isParentChecked = false
      }
    })
  }

  if (!isAllByUserPref) {
    traverse(nodeList)
  }

  return params
}

export const getLocationNameById = (
  nodes: LocationTreeNode[],
  id: number
): string => {
  let name = ''

  const traverse = (current: LocationTreeNode[]) => {
    forEach(current, (item) => {
      if (item.id === id) {
        name = item.name
      }

      if (item.children && item.children.length) {
        traverse(item.children)
      }
    })
  }

  traverse(nodes)

  return name
}

export const getAssignedLocationsLabel = (
  locationFilter: LocationFilter,
  isAllSelected: boolean
): string => {
  if (isAllSelected) {
    return LOCATION_LABELS.allProperties
  }
  const { selectedBuildings, selectedName } = locationFilter
  if (!selectedBuildings || selectedBuildings.length === 0) {
    return ''
  }
  if (selectedBuildings.length > 1) {
    return LOCATION_LABELS.multipleProperties
  }
  return selectedName
}
